import React, { useState, useEffect } from "react";
import { Box, IconButton, Typography, Button } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import "./ProductListing.css";
import { ProductListingProps } from "../../types/ProductList";
import editIcon from "../../assets/edit-icon.svg";
import EyeComponent from "../eyeComponent/EyeComponent";
import { fetchProductListingData } from "../../api/ProductListAPI";
import Breadcrumbs from "../../common/Breadcrumbs";
import { validateTokenAndLogoutIfExpired } from "../../common/getValidToken";
import { useRole } from "../../context/RoleContext";
import { mapProducts } from "../../utils/productListMapping";
import { productEndPoints } from "../../constants/endpoints";
import Toast from "../../common/Toast";
import CustomDropdown from "../../common/CustomDropdown";
import Info from "../../assets/info.svg";

export const ProductListing = () => {
  const { role } = useRole();
  const navigate = useNavigate();
  const location = useLocation();
  const [sortOrder, setSortOrder] = useState<string>("latest");
  const [products, setProducts] = useState<ProductListingProps[]>([]);
  const [sortedProducts, setSortedProducts] = useState<ProductListingProps[]>(
    []
  );
  const [hoveredProductIds, setHoveredProductIds] = useState<Set<string>>(
    new Set()
  );

  const orgId = localStorage.getItem("orgId");

  // Fetching product listing data on component mount
  useEffect(() => {
    setError(true);
    setToastMessage("Failed to fetch Products");
    const fetchData = async () => {
      try {
        const data = await fetchProductListingData(
          productEndPoints.getProducts
        );
        const mappedData = mapProducts(data.products);
        if (data?.products) {
          setError(false);
        }

        setProducts(mappedData);
      } catch (error) {
        console.error("Error fetching product data:", error);
      }
    };

    fetchData();
  }, []);

  // Sorting data after fetching
  useEffect(() => {
    const sortData = () => {
      const sorted = [...products].sort((a, b) => {
        if (sortOrder === "latest")
          return new Date(b.date).getTime() - new Date(a.date).getTime();
        if (sortOrder === "oldest")
          return new Date(a.date).getTime() - new Date(b.date).getTime();
        return 0;
      });
      setSortedProducts(sorted);
    };

    sortData();
  }, [sortOrder, products]);

  const handleImageClick = (id: string) => {
    navigate(`product/${id}`);
  };

  const handleEdit = (id: string) => {
    navigate(`/${orgId}/seller/edit-product/${id}`);
  };
  const handleAddProduct = () => {
    navigate(`/${orgId}/seller/create-new-product`); // Navigates to the "create-new-product" route
  };

  const isSellerRoute = location.pathname.includes("/seller/products");

  const handleMouseEnter = (id: string) => {
    setHoveredProductIds((prev) => new Set(prev.add(id)));
  };

  const handleMouseLeave = (id: string) => {
    const newHoveredProductIds = new Set(hoveredProductIds);
    newHoveredProductIds.delete(id);
    setHoveredProductIds(newHoveredProductIds);
  };

  const getLinks = () => {
    if (role === "seller" || location.pathname.startsWith("/seller")) {
      return ["/seller/create-new-customer", "/seller/products"];
    }
    if (role === "customer" || location.pathname.startsWith("/customer")) {
      return ["/customer/home", "/customer/products"];
    }
  };

  const [error, setError] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);

  const handleSortChange = (selectedValue: string) => {
    setSortOrder(selectedValue); // Update sortOrder using the setter
  };

  const sortOptions = [
    { value: "oldest", label: "Oldest", icon: "oldest-icon.svg" },
    { value: "latest", label: "Latest", icon: "latest-icon.svg" },
  ];
  return (
    <div>
      <Breadcrumbs paths={["Home", "Products"]} links={getLinks()} />

      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        toastMessage={toastMessage}
        error={error}
      />

      <Box className="filter-container">
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
          }}
        >
          <Box className="filter-left">
            <Typography variant="body1" className="sortBy">
              Sort by:
            </Typography>
            <Box sx={{ width: "166px", height: "41px" }}>
              <CustomDropdown
                label="Latest"
                value={sortOrder}
                options={sortOptions}
                onChange={handleSortChange}
              />
            </Box>
          </Box>
          {/* Show notice only in the customer portal */}
          {!isSellerRoute && (
            <Box className="notice-message">
              <img src={Info} />
              <Typography variant="body2">
                <span className="note">Please Note:</span> Orders placed before
                3 PM today: Next business day delivery & Orders placed after 3
                PM today: Delivery within 2 business days.
              </Typography>
            </Box>
          )}
        </Box>
        {isSellerRoute && (
          <Button
            variant="contained"
            onClick={handleAddProduct}
            className="add-more-product"
          >
            Add More Products
          </Button>
        )}
      </Box>

      <Box className="container">
        <Box className="products-wrapper">
          {sortedProducts.map((item) => (
            <Box
              key={item.id}
              className={`product-card ${item.isOutOfStock ? "out-of-stock" : ""}`}
              onMouseEnter={() => handleMouseEnter(item.id)}
              onMouseLeave={() => handleMouseLeave(item.id)}
            >
              {/* Product Image */}
              <img
                src={item.images}
                alt={item.name}
                className="product-image"
                onClick={() => handleImageClick(item.id)}
              />

              {/* Product Name */}
              <Box className="product-name-wrapper">
                <Typography variant="h6" className="product-name">
                  {item.name.split(", ").map((part, index) => (
                    <React.Fragment key={index}>
                      {part}
                      {index === 1 && <br />}
                    </React.Fragment>
                  ))}
                </Typography>
                <Typography variant="subtitle2" className="abv-text">
                  {item.abv && `(${item.abv}% ABV) IBU - ${item.ibu}`}
                </Typography>
              </Box>

              {/* Price Section */}
              <Box className="price-section">
                <Typography
                  variant="body1"
                  className={`product-price ${
                    item.isOutOfStock && !isSellerRoute
                      ? "out-of-stock-price"
                      : ""
                  }`}
                >
                  ₹ {new Intl.NumberFormat().format(Number(item.price))}/L
                </Typography>
                {/* </Box> */}

                {/* Seller Route Specific */}
                <Box className="right-section">
                  {isSellerRoute ? (
                    <Box className="edit-eye-icons">
                      <EyeComponent
                        productID={item?.id}
                        disabled={item.disabled}
                      />
                      <IconButton onClick={() => handleEdit(item?.id)}>
                        <img
                          src={editIcon}
                          alt="edit-icon"
                          className="edit-button"
                        />
                      </IconButton>
                    </Box>
                  ) : (
                    item.isOutOfStock && (
                      <Typography
                        variant="subtitle2"
                        className="out-of-stock-message"
                      >
                        Out of Stock
                      </Typography>
                    )
                  )}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </div>
  );
};

export default ProductListing;
